<template>
    <div class="reservation-assigned-tables-editor" @click.stop>
        <div>
            <MultiEntityInputComponent
                :dataSource="tablesDataSource"
                v-model="selectedTables"
                ref="multiEntityInputComponent"
            />
            <!-- <div>Selected: {{ selectedTables.join(", ") }}</div> -->
        </div>
        <div class="action-buttons">
            <span class="cancel-assignment-btn" :class="{ disabled: areButtonsDisabled }" @click="cancelEdit">
                {{ translations.general.cancel }}
            </span>
            <span class="save-assignment-btn" :class="{ disabled: areButtonsDisabled }" @click="saveChanges">
                {{ translations.general.save }}
            </span>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import type { IMultiEntityLinkDataSource } from "@/data/dao/service/BaseServiceDao";
    import { OperatorDao } from "@/data/dao/service/OperatorDao";
    import type { IReservationTablesToAssign } from "@/data/model/Order";
    import type { TableAssignment } from "@/operator/data/Reservations";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, ref } from "vue";

    import MultiEntityInputComponent from "@/components/elements/MultiEntityInputComponent.vue";

    const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);

    const props = defineProps<{
        orderId: string;
        orderItemId: string;
        dateFrom: Date;
        dateTo: Date;
        tableAssignments: TableAssignment[];
    }>();

    const emit = defineEmits(["reservationDetailsChanged", "cancelEditing"]);

    const areButtonsDisabled = computed(() => isProcessing.value);
    const selectedTables = ref<string[]>([]);
    const tablesForReservationAssignment = ref<IReservationTablesToAssign>();
    const multiEntityInputComponent = ref<InstanceType<typeof MultiEntityInputComponent> | null>(null);
    const isProcessing = ref(false);

    for (const tableAssignment of props.tableAssignments) {
        selectedTables.value.push(tableAssignment.id);
    }

    let tablesPromise: Promise<void> | null = null;

    function cancelEdit() {
        emit("cancelEditing");
    }

    async function saveChanges() {
        try {
            isProcessing.value = true;
            const result = await operatorDao.updateAssignedTables({
                orderId: props.orderId,
                orderItemId: props.orderItemId,
                tableAssignments: selectedAssignments.value,
            });
            emit("reservationDetailsChanged", result);
        } catch (ex) {
            logError(ex);
            Popup.show({
                title: translations.general.error,
                message: translations.general.operationFailed,
            });
        } finally {
            isProcessing.value = false;
        }
    }

    // todo: cases to cover
    // - no tables assigned - do nothing
    // - no tables assigned - but were assigned before - unassign all tables
    // - if tables combination selected - do not allow to assign other tables
    // - if table selected - allow up to 10 tables to be selected

    const currentTableAssignments = computed(() => {
        if (tablesForReservationAssignment.value == null) {
            return [];
        }

        const result: TableAssignment[] = [];

        for (const table of tablesForReservationAssignment.value.tables) {
            const tableAssignment = {
                id: table.tableId,
                name: table.tableName,
                isCombination: false,
            };
            result.push(tableAssignment);
        }

        for (const combination of tablesForReservationAssignment.value.combinations) {
            const tableCombinationAssignment = {
                id: combination.combinationId,
                name: combination.tables.map((x) => x.tableName).join(", "),
                isCombination: true,
            };
            result.push(tableCombinationAssignment);
        }

        return result;
    });
    const selectedAssignments = computed(() => {
        return currentTableAssignments.value.filter((x) => selectedTables.value.includes(x.id));
    });
    const isMaxTablesSelected = computed(() => {
        return selectedAssignments.value.filter((x) => !x.isCombination).length >= 10;
    });
    const isCombinationSelected = computed(() => {
        return selectedAssignments.value.filter((x) => x.isCombination).length > 0;
    });
    const isTableSelected = computed(() => {
        return selectedAssignments.value.filter((x) => !x.isCombination).length > 0;
    });

    function loadTables(): Promise<void> {
        if (!tablesPromise) {
            tablesPromise = getTables();
        }
        return tablesPromise;
    }

    async function getTables() {
        const data = await operatorDao.getTablesForReservationAssignment({
            orderId: props.orderId,
            orderItemId: props.orderItemId,
            dateFrom: props.dateFrom.toISOString(),
            dateTo: props.dateTo.toISOString(),
        });

        tablesForReservationAssignment.value = data;
    }

    const tablesDataSource: IMultiEntityLinkDataSource = {
        search: async (excludedIds, term) => {
            await loadTables();

            let result = [...currentTableAssignments.value];
            if (isTableSelected.value) {
                result = result.filter((x) => !x.isCombination || selectedTables.value.includes(x.id));
            }
            if (isCombinationSelected.value) {
                result = result.filter((x) => selectedTables.value.includes(x.id));
            }
            if (isMaxTablesSelected.value) {
                result = result.filter((x) => selectedTables.value.includes(x.id));
            }

            return result.filter(
                (x) => !excludedIds.includes(x.id) && x.name.toLowerCase().includes(term?.toLowerCase() ?? "")
            );
        },
    };
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../../assets/styles/scss/shared/_mixins.scss";

    .reservation-assigned-tables-editor {
        cursor: default;

        .action-buttons {
            display: flex;
            gap: 0.5em;
            justify-content: flex-end;
            margin-top: 0.5em;

            span {
                // font-size: 0.875em;
                font-size: 1em;
                cursor: pointer;

                &.save-assignment-btn {
                    color: colors.$tastierGreen;
                    color: var(--colorPrimary);

                    @include mixins.hover-and-active {
                        color: colors.$greenHover;
                        color: var(--colorPrimaryHover);
                    }
                }

                &.cancel-assignment-btn {
                    color: colors.$tastierOrange;

                    @include mixins.hover-and-active {
                        color: colors.$orangeHover;
                    }
                }
            }
        }

        .multi-entity-input.open {
            border-color: colors.$green !important;

            .search {
                border-color: colors.$green !important;
            }
        }
    }
</style>
